/**
 * Drag карты к центру взятки для онлайн open 1×1 — паритет с жестами
 * `BelotArmenianLocalLobby` (эллипс, гистерезис, snap-back, подавление клика после drag).
 *
 * @module components/online/hooks/useBelotOnlineHeroDragPlay
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import {
  BELOT_TRICK_DRAG_MOVE_PX,
  BELOT_TRICK_DROP_THRESHOLD_IN,
  BELOT_TRICK_DROP_THRESHOLD_OUT,
  getTrickCenterDropMetric,
} from '../../belot/belotTrickDropMetric';

/**
 * @typedef {{ card: number, from: 'hand'|'table', columnIndex?: number }} OpenPlayPayload
 */

/**
 * @param {object} args
 * @param {React.MutableRefObject<HTMLElement|null>} args.trickCenterRef
 * @param {boolean} args.enabled
 * @param {boolean} args.isInputBlocked
 * @param {(cardId: number) => { rank: string, suit: string }} args.cardIdToPlaying
 * @param {(payload: OpenPlayPayload, dragDropFromPoint: { x: number, y: number }|null) => void} args.onCommitPlay
 */
export function useBelotOnlineHeroDragPlay({
  trickCenterRef,
  enabled,
  isInputBlocked,
  cardIdToPlaying,
  onCommitPlay,
}) {
  const onCommitPlayRef = useRef(onCommitPlay);
  onCommitPlayRef.current = onCommitPlay;

  const dragPlayRef = useRef(null);
  const dragPointerRef = useRef({ x: 0, y: 0 });
  const dragMoveRafRef = useRef(null);
  const dragDropActiveRef = useRef(false);
  const dragCancelTimeoutRef = useRef(null);
  const dragSnapTweenRef = useRef(null);
  const suppressNextCardClickRef = useRef(false);
  const suppressClickTimeoutRef = useRef(null);

  const dragGhostRef = useRef(null);
  const [dragPlayCard, setDragPlayCard] = useState(null);

  const clearDragPlayCard = useCallback(() => {
    if (dragMoveRafRef.current != null) {
      window.cancelAnimationFrame(dragMoveRafRef.current);
      dragMoveRafRef.current = null;
    }
    if (dragCancelTimeoutRef.current != null) {
      window.clearTimeout(dragCancelTimeoutRef.current);
      dragCancelTimeoutRef.current = null;
    }
    if (dragSnapTweenRef.current) {
      dragSnapTweenRef.current.kill();
      dragSnapTweenRef.current = null;
    }
    dragDropActiveRef.current = false;
    dragPlayRef.current = null;
    setDragPlayCard(null);
  }, []);

  const armSuppressNextCardClick = useCallback((ms = 320) => {
    suppressNextCardClickRef.current = true;
    if (suppressClickTimeoutRef.current != null) {
      window.clearTimeout(suppressClickTimeoutRef.current);
    }
    suppressClickTimeoutRef.current = window.setTimeout(() => {
      suppressClickTimeoutRef.current = null;
      suppressNextCardClickRef.current = false;
    }, ms);
  }, []);

  const getPointerDropMetric = useCallback((x, y) => {
    return getTrickCenterDropMetric(trickCenterRef.current, x, y);
  }, [trickCenterRef]);

  const onPlayPointerDown = useCallback(
    (event, /** @type {OpenPlayPayload} */ payload, canPlay) => {
      if (!canPlay || !enabled || isInputBlocked) return;
      event.preventDefault();
      event.stopPropagation();
      if (dragCancelTimeoutRef.current != null) {
        window.clearTimeout(dragCancelTimeoutRef.current);
        dragCancelTimeoutRef.current = null;
      }
      if (dragSnapTweenRef.current) {
        dragSnapTweenRef.current.kill();
        dragSnapTweenRef.current = null;
      }
      const cardRect = event.currentTarget?.getBoundingClientRect?.();
      const originX = cardRect ? cardRect.left + cardRect.width / 2 : event.clientX;
      const originY = cardRect ? cardRect.top + cardRect.height / 2 : event.clientY;
      const { rank, suit } = cardIdToPlaying(payload.card);
      dragDropActiveRef.current = false;
      dragPlayRef.current = {
        pointerId: event.pointerId,
        payload,
        originX,
        originY,
        startX: event.clientX,
        startY: event.clientY,
        moved: false,
      };
      setDragPlayCard({
        cardId: payload.card,
        x: event.clientX,
        y: event.clientY,
        originX,
        originY,
        rank,
        suit,
        dropActive: false,
        returning: false,
      });
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    },
    [enabled, isInputBlocked, cardIdToPlaying],
  );

  const onPlayPointerMove = useCallback(
    (event) => {
      const d = dragPlayRef.current;
      if (!d || event.pointerId !== d.pointerId) return;
      const dx = event.clientX - d.startX;
      const dy = event.clientY - d.startY;
      const moved = Math.hypot(dx, dy) >= BELOT_TRICK_DRAG_MOVE_PX;
      if (moved && !d.moved) d.moved = true;
      dragPointerRef.current = { x: event.clientX, y: event.clientY };
      if (dragMoveRafRef.current != null) return;
      dragMoveRafRef.current = window.requestAnimationFrame(() => {
        dragMoveRafRef.current = null;
        const current = dragPointerRef.current;
        const metric = getPointerDropMetric(current.x, current.y);
        const nextDropActive =
          d.moved && (dragDropActiveRef.current ? metric <= BELOT_TRICK_DROP_THRESHOLD_IN : metric <= BELOT_TRICK_DROP_THRESHOLD_OUT);
        dragDropActiveRef.current = nextDropActive;
        setDragPlayCard((prev) =>
          prev
            ? {
                ...prev,
                x: current.x,
                y: current.y,
                dropActive: nextDropActive,
                returning: false,
              }
            : prev,
        );
      });
    },
    [getPointerDropMetric],
  );

  const onPlayPointerUp = useCallback(
    (event) => {
      const d = dragPlayRef.current;
      if (!d || event.pointerId !== d.pointerId) return;
      const metric = getPointerDropMetric(event.clientX, event.clientY);
      const dropActive = dragDropActiveRef.current ? metric <= BELOT_TRICK_DROP_THRESHOLD_IN : metric <= BELOT_TRICK_DROP_THRESHOLD_OUT;
      const moved = Boolean(d.moved);
      const payload = d.payload;
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
      if (moved && !dropActive) {
        const ghostEl = dragGhostRef.current;
        const originX = d.originX ?? event.clientX;
        const originY = d.originY ?? event.clientY;
        armSuppressNextCardClick();
        if (ghostEl) {
          dragSnapTweenRef.current = gsap.to(ghostEl, {
            left: originX,
            top: originY,
            duration: 0.22,
            ease: 'power2.out',
            onComplete: () => {
              dragSnapTweenRef.current = null;
              clearDragPlayCard();
            },
          });
          setDragPlayCard((prev) => (prev ? { ...prev, returning: true, dropActive: false } : prev));
          return;
        }
      }
      clearDragPlayCard();
      if (!moved) {
        armSuppressNextCardClick(220);
        onCommitPlayRef.current(payload, null);
        return;
      }
      armSuppressNextCardClick();
      if (dropActive) {
        onCommitPlayRef.current(payload, { x: event.clientX, y: event.clientY });
      }
    },
    [armSuppressNextCardClick, clearDragPlayCard, getPointerDropMetric],
  );

  const onPlayPointerCancel = useCallback(() => {
    const d = dragPlayRef.current;
    if (!d) {
      clearDragPlayCard();
      return;
    }
    if (dragCancelTimeoutRef.current != null) {
      window.clearTimeout(dragCancelTimeoutRef.current);
    }
    dragCancelTimeoutRef.current = window.setTimeout(() => {
      dragCancelTimeoutRef.current = null;
      clearDragPlayCard();
    }, 90);
  }, [clearDragPlayCard]);

  const onPlayCardClick = useCallback(
    (/** @type {OpenPlayPayload} */ payload, canPlay) => {
      if (!canPlay || !enabled || isInputBlocked) return;
      if (suppressNextCardClickRef.current) {
        suppressNextCardClickRef.current = false;
        if (suppressClickTimeoutRef.current != null) {
          window.clearTimeout(suppressClickTimeoutRef.current);
          suppressClickTimeoutRef.current = null;
        }
        return;
      }
      onCommitPlayRef.current(payload, null);
    },
    [enabled, isInputBlocked],
  );

  useEffect(
    () => () => {
      if (suppressClickTimeoutRef.current != null) {
        window.clearTimeout(suppressClickTimeoutRef.current);
        suppressClickTimeoutRef.current = null;
      }
      if (dragMoveRafRef.current != null) {
        window.cancelAnimationFrame(dragMoveRafRef.current);
        dragMoveRafRef.current = null;
      }
      if (dragCancelTimeoutRef.current != null) {
        window.clearTimeout(dragCancelTimeoutRef.current);
        dragCancelTimeoutRef.current = null;
      }
      if (dragSnapTweenRef.current) {
        dragSnapTweenRef.current.kill();
        dragSnapTweenRef.current = null;
      }
    },
    [],
  );

  return {
    dragPlayCard,
    dragGhostRef,
    onPlayPointerDown,
    onPlayPointerMove,
    onPlayPointerUp,
    onPlayPointerCancel,
    onPlayCardClick,
    clearDragPlayCard,
  };
}
